// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Container, Grid, Typography, Card, CardContent, Avatar, Stack, Chip } from '@mui/material';

// third party
import { motion } from 'framer-motion';

// icons
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CodeIcon from '@mui/icons-material/Code';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ApiIcon from '@mui/icons-material/Api';

// ==============================|| LANDING - FEATURES SHOWCASE SECTION ||============================== //

const FeaturesShowcase = () => {
    const theme = useTheme();

    const features = [
        {
            icon: <CloudQueueIcon sx={{ fontSize: 40 }} />,
            title: 'Multi-Cloud Storage',
            description: 'Tích hợp liền mạch với S3, MinIO, Google Cloud Storage và Azure Blob. Quản lý tập trung mọi nguồn lưu trữ.',
            color: '#3B82F6',
            tags: ['S3', 'MinIO', 'GCS', 'Azure']
        },
        {
            icon: <CodeIcon sx={{ fontSize: 40 }} />,
            title: 'JupyterHub Computing',
            description: 'Môi trường phát triển tích hợp với GPU support. Tạo và quản lý notebooks, chạy mô hình ML/AI hiệu quả.',
            color: '#8B5CF6',
            tags: ['GPU', 'Notebooks', 'Python', 'R']
        },
        {
            icon: <WorkspacesIcon sx={{ fontSize: 40 }} />,
            title: 'Airflow Orchestration',
            description: 'Tự động hóa quy trình xử lý dữ liệu với Apache Airflow. Lập lịch, giám sát và quản lý workflows dễ dàng.',
            color: '#10B981',
            tags: ['DAGs', 'Scheduling', 'Monitoring']
        },
        {
            icon: <DataObjectIcon sx={{ fontSize: 40 }} />,
            title: 'MongoDB Integration',
            description: 'Lưu trữ và truy vấn dữ liệu phi cấu trúc với MongoDB. Hỗ trợ multi-tenancy và bảo mật cao.',
            color: '#F59E0B',
            tags: ['NoSQL', 'Documents', 'Real-time']
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
            title: 'Enterprise Security',
            description: 'Bảo mật đa lớp với mã hóa end-to-end, audit logs chi tiết và quản lý quyền truy cập granular.',
            color: '#EF4444',
            tags: ['Encryption', 'RBAC', 'Audit']
        },
        {
            icon: <SpeedIcon sx={{ fontSize: 40 }} />,
            title: 'High Performance',
            description: 'Xử lý hàng triệu requests với hiệu suất cao. Caching thông minh và tối ưu hóa truy vấn tự động.',
            color: '#14B8A6',
            tags: ['Redis', 'Caching', 'Optimization']
        },
        {
            icon: <ApiIcon sx={{ fontSize: 40 }} />,
            title: 'API Gateway Platform',
            description: 'Quản lý API chuyên nghiệp với rate limiting, authentication, monitoring và analytics chi tiết.',
            color: '#6366F1',
            tags: ['Rate Limiting', 'Auth', 'Analytics', 'Monitoring']
        }
    ];

    return (
        <Box sx={{ py: 8 }}>
            <Container>
                <motion.div
                    initial={{ opacity: 0, translateY: 20 }}
                    whileInView={{ opacity: 1, translateY: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <Stack spacing={2} alignItems="center" sx={{ mb: 8 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 1.5
                            }}
                        >
                            Tính năng nổi bật
                        </Typography>
                        <Typography
                            variant="h2"
                            align="center"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                            }}
                        >
                            Giải pháp toàn diện cho dữ liệu doanh nghiệp
                        </Typography>
                        <Typography variant="h5" align="center" color="text.secondary" sx={{ maxWidth: 800 }}>
                            DataHub cung cấp đầy đủ công cụ và tính năng để quản lý, xử lý và phân tích dữ liệu quy mô lớn
                        </Typography>
                    </Stack>
                </motion.div>

                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                            <motion.div
                                initial={{ opacity: 0, translateY: 20 }}
                                whileInView={{ opacity: 1, translateY: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Card
                                    sx={{
                                        height: '100%',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid',
                                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow:
                                                theme.palette.mode === 'dark'
                                                    ? '0 20px 40px rgba(0,0,0,0.3)'
                                                    : '0 20px 40px rgba(0,0,0,0.1)',
                                            borderColor: feature.color,
                                            '& .feature-icon': {
                                                transform: 'scale(1.1) rotate(5deg)'
                                            }
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 4 }}>
                                        <Avatar
                                            className="feature-icon"
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                bgcolor: feature.color + '20',
                                                color: feature.color,
                                                mb: 3,
                                                transition: 'transform 0.3s ease'
                                            }}
                                        >
                                            {feature.icon}
                                        </Avatar>
                                        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                                            {feature.description}
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {feature.tags.map((tag, tagIndex) => (
                                                <Chip
                                                    key={tagIndex}
                                                    label={tag}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: feature.color + '20',
                                                        color: feature.color,
                                                        fontWeight: 500,
                                                        mb: 1
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                {/* Integration Partners */}
                <motion.div
                    initial={{ opacity: 0, translateY: 20 }}
                    whileInView={{ opacity: 1, translateY: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <Box sx={{ mt: 12, textAlign: 'center' }}>
                        <Stack spacing={4} alignItems="center">
                            <Box>
                                <IntegrationInstructionsIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                                    Tích hợp với hệ sinh thái công nghệ hiện đại
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Kết nối dễ dàng với các công cụ và nền tảng bạn đang sử dụng
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center" useFlexGap>
                                {['AWS', 'Google Cloud', 'Azure', 'Docker', 'Kubernetes', 'PostgreSQL', 'Redis', 'Grafana'].map((tech) => (
                                    <Chip
                                        key={tech}
                                        label={tech}
                                        sx={{
                                            px: 3,
                                            py: 2,
                                            fontSize: '1rem',
                                            fontWeight: 500,
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.main,
                                                color: 'white'
                                            }
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default FeaturesShowcase;
